import axiosInstance from "../components/api/axiosInstance";

/**
 * Fetch subtasks for a task.
 * GET /subtasks?task_id=X&status_id=Y&priority=Z
 * @param {number} taskId - Task ID
 * @param {number|null} statusId - Optional status ID filter
 * @param {string|null} priority - Optional priority filter (e.g. "Low", "High")
 * @returns {Promise<Array>} Array of subtasks
 */
export async function fetchSubTasksByTaskId(taskId, statusId = null, priority = null) {
  if (!taskId) return [];
  try {
    const params = { task_id: Number(taskId) };
    if (statusId != null && statusId !== undefined && statusId !== "") {
      params.status_id = Number(statusId);
    }
    if (priority != null && priority !== undefined && priority !== "") {
      params.priority = String(priority).toLowerCase();
    }
    const response = await axiosInstance.get("/subtasks", { params });
    const raw = response.data;
    if (Array.isArray(raw)) return raw;
    if (raw?.data && Array.isArray(raw.data)) return raw.data;
    if (raw?.sub_tasks && Array.isArray(raw.sub_tasks)) return raw.sub_tasks;
    return [];
  } catch (err) {
    console.error("Error fetching subtasks:", err);
    return [];
  }
}

/**
 * Fetch a single subtask by ID.
 * GET /subtasks/:subTaskId
 * Response: { Sub_Task_Id, Task_Id, Task_Title, Title, Description, Status, Status_Id, Priority, Start_Date, End_Date, Created_By, Created_By_Name, Created_On, Updated_By, Updated_By_Name, Updated_On }
 * @param {number} subTaskId - Sub Task ID
 * @returns {Promise<Object>} Sub task object
 */
export async function fetchSubTaskById(subTaskId) {
  if (!subTaskId) return null;
  try {
    const response = await axiosInstance.get(`/subtasks/${subTaskId}`);
    const raw = response.data;
    return raw?.data ?? raw;
  } catch (err) {
    console.error("Error fetching subtask:", err);
    return null;
  }
}

/**
 * Create a new subtask.
 * POST /create-subtask
 * @param {Object} payload - { Task_Id, Story_Id, Title, Description, Priority, Start_Date, End_Date, Created_By, Assigned_User_Ids? }
 * @returns {Promise<Object>} Created subtask
 */
export async function createSubTask(payload) {
  const response = await axiosInstance.post("/create-subtask", payload);
  return response.data?.data ?? response.data ?? response;
}

/**
 * Update a subtask.
 * PUT /update-subtask/:subTaskId
 * Request: { Task_Id, Story_Id, Title, Description, Priority, Start_Date, End_Date, Updated_By, Assigned_User_Ids? }
 * @param {number} subTaskId - Sub Task ID
 * @param {Object} payload - Update payload
 * @returns {Promise<Object>} Updated subtask
 */
export async function updateSubTask(subTaskId, payload) {
  const response = await axiosInstance.put(`/update-subtask/${subTaskId}`, payload);
  const raw = response.data;
  return raw?.data ?? raw;
}

/**
 * Delete a subtask.
 * DELETE /subtask/:subTaskId
 * Response: { message: "Subtask deleted successfully" }
 * @param {number} subTaskId - Sub Task ID
 * @returns {Promise<Object>} Response
 */
export async function deleteSubTask(subTaskId) {
  const response = await axiosInstance.delete(`/subtask/${subTaskId}`);
  return response.data;
}
